import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription, forkJoin } from 'rxjs';

import * as _ from 'underscore';

import { PersonSearchService } from './person-search.service';
import { PersonInsightsData } from '../hire-person-search/hire-insights/PersonInsightsData';
import { AggregationRequest } from '../common-interfaces-classes/AggregationRequest';
import { CallStage } from '../enums/CallStage';
import { AggReqObjectHandlerService } from './agg-req-object-handler.service';
import { FilterDataHandlerService } from './filter-data-handler.service';
import { InsightsDataService } from './insights-data.service';
import { ExtraInfo } from '../person-search-filter/ExtraInfo';
import { LoaderEventsService } from './loader-events.service';
import { LoaderType } from '../enums/LoaderType';
import PersonSearchResponse from '../common-interfaces-classes/PersonSearchResponse';
import { LocalStorageService } from './local-storage.service';
import { SessionStorageService } from './session-storage.service';
import { SimilarCandidateService } from './similar-candidate.service';
import { FilterParams } from '../common-interfaces-classes/FilterParams';
import { GeneralConstants } from '../constants/GeneralConstants';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, retry } from 'rxjs/operators';
import { TalentInsightsReqHandlerService } from './talent-insights-req-handler.service';
import { RedirectData } from '../common-interfaces-classes/RedirectData';
import { JdDataService } from '../jd-data.service';
import { SimilarCandidateDetails } from '../common-interfaces-classes/SimilarCandidate';
import { FusionChartsService } from './fusion-charts.service';
import { AiSourcingService } from './ai-sourcing.service';
import { AiSourcingHandlerService } from './ai-sourcing-handler.service';
import { DataService } from '../data.service';

type StatusMap = {
  jobTitleStatus: string;
  companyStatus: string;
  industryStatus: string;
  yearsOfExperienceType: string;
};

@Injectable({
  providedIn: "root",
})
export class AggregationService {

  personSearchAggSub: Subscription = null;

  constructor(
    private personSearchService: PersonSearchService,
    private aggReqObjHandler: AggReqObjectHandlerService,
    private filterDataHandlerService: FilterDataHandlerService,
    private insightsDataService: InsightsDataService,
    private loaderEventService: LoaderEventsService,
    private localStorageService: LocalStorageService,
    private similarCandidateService: SimilarCandidateService,
    private activatedRoute: ActivatedRoute,
    private talentInsightsReqHandlerService: TalentInsightsReqHandlerService,
    private sessionStorageService: SessionStorageService,
    private jdDataService: JdDataService,
    private route: ActivatedRoute,
    private fusionChartService: FusionChartsService,
    private aiSourcingService: AiSourcingService,
    private aiSourcingHandlerService: AiSourcingHandlerService,
    private router: Router,
    private dataService: DataService
  ) {}

  private statsSubject: Subject<[any, any, any]> = new Subject();
  private changedJobSubject: Subject<any> = new Subject();
  private changedJobResetSubject: Subject<any> = new Subject();


  get statsUpdateEvent(): Observable<[any, any, any]> {
    return this.statsSubject.asObservable();
  }

  get changedJobStatsEvent(): Observable<any> {
    return this.changedJobSubject.asObservable();
  }

  changedJobStats(statsObject){
    this.changedJobSubject.next(statsObject);
  }

  get setResetChangedJob(): Observable<any> {
    return this.changedJobResetSubject.asObservable();
  }

  changeJobResetEmit(){
    this.changedJobResetSubject.next('reset');
  }

  emitStatsObject(statsObject: [any, any, any]) {
    this.statsSubject.next(statsObject);
  }

  private insightsDataSubject: Subject<any> = new Subject();

  get insightsDataEvent(): Observable<any> {
    return this.insightsDataSubject.asObservable();
  }

  emitInsightsObject(insightsData: any) {
    this.insightsDataSubject.next(insightsData);
  }

  private recCandidateDataSubject: Subject<PersonSearchResponse> = new Subject();

  get recCandidateDataEvent(): Observable<PersonSearchResponse> {
    return this.recCandidateDataSubject.asObservable();
  }

  get geoIpLocation() {
    const location = JSON.parse(this.localStorageService.getItem('userGeoIpLocation'));
    const country = location?.country?.toLowerCase();
    const city = location?.city?.toLowerCase();
    const region = location?.region?.toLowerCase();
    const continent = location?.continent?.toLowerCase();
    return { location: `${city || ''}${city && (region || country) ? ', ' : ''}${region || ''}${region && country ? ', ' : ''}${country || ''}`, continent }
  }

  emitRecCandidateObject(recCandidateData: PersonSearchResponse) {
    this.recCandidateDataSubject.next(recCandidateData);
  }

  processAggregationResponse(
    pIData: PersonInsightsData,
    resp: any,
    reqObj: AggregationRequest
  ) {
    const response = Object.assign({}, resp);
    const personInsightsData = Object.assign({}, pIData);
    const localReqObj = Object.assign({}, reqObj);
    if(localReqObj.aggregatetype === 'nationality'){
      // personInsightsData.dataSourceNationality =
      // this.fusionChartService.nationalityChart(response?.data?.aggregations?.nationality || [], response?.data?.totalresultscount);
    } else if(localReqObj.aggregatetype === 'ethnicity'){
      // personInsightsData.dataSourceEthnicity =
      // this.fusionChartService.ethnicityChart(response?.data?.aggregations?.ethnicity || [], response?.data?.totalresultscount);
    } else if (localReqObj.aggregatetype === "gender") {
      personInsightsData.dataSourceGender =
        this.createDataSourceGender(response);
    } else if (localReqObj.aggregatetype === "currentyearsofexperience") {
      personInsightsData.dataSourceExpInCurrentRole =
        this.createExpInCurrentRole(response);
    } else if (
      localReqObj.aggregatetype === "totalyearsofexperience" ||
      localReqObj.aggregatetype === "averageyearsofexperience"
    ) {
      personInsightsData.dataSourceCurrentExperience =
        this.createCurrentExperience(response, localReqObj.aggregatetype);
    } else if (localReqObj.aggregatetype === "seniority") {
      personInsightsData.dataSourceSeniority =
        this.createSeniorityData(response);
    } else if (localReqObj.aggregatetype === "location_name-location_continent") {
      personInsightsData.topLocations = response['location_name-location_continent'];
      personInsightsData.dataSourceWorkCountry =
        this.createWorkCountryData(response);
    } else if (
      localReqObj.aggregatetype === "current_company" ||
      localReqObj.aggregatetype === "company" ||
      localReqObj.aggregatetype === "pastcompany"
    ) {
      personInsightsData.dataSourceCurrentCompany = this.createCompanyData(
        !!response[localReqObj.aggregatetype]
          ? response[localReqObj.aggregatetype].slice(0, 5)
          : []
      );
    } else if (
      localReqObj.aggregatetype === "jobtitle" ||
      localReqObj.aggregatetype === "current_jobtitle" ||
      localReqObj.aggregatetype === "pastjobtitle"
    ) {
      personInsightsData.titles = response[localReqObj.aggregatetype]?.slice(0, 5);
    } else if (localReqObj.aggregatetype === "school") {
      personInsightsData.school =
        response[localReqObj.aggregatetype]?.slice(0, 5) || [];
    } else if (localReqObj.aggregatetype === "skills") {
      personInsightsData.skills =
        response[localReqObj.aggregatetype]?.slice(0, 5) || [];
    } else if (localReqObj.aggregatetype === "fieldofstudy") {
      personInsightsData.fieldOfStudy =
        response[localReqObj.aggregatetype]?.slice(0, 5) || [];
    } else if (
      localReqObj.aggregatetype === "current_industry" ||
      localReqObj.aggregatetype === "industry" ||
      localReqObj.aggregatetype === "pastindustry"
    ) {
      personInsightsData.currentIndustry =
        response[localReqObj.aggregatetype]?.slice(0, 5) || [];
    }
    return personInsightsData;
  }

  getStatsAggs() {
      forkJoin([
        // this.getAggregationReport("likelytomove"),
        this.getAggregationReport("total_tenure_median"),
        this.getAggregationReport("gender"),
        this.getAggregationReport("changedjobs")
      ]).subscribe(res => this.emitStatsObject(res));
  }

  getStatsAggsAISourcing(projectnumber: string) {
    forkJoin([
      this.getAggregationReportAI('total_tenure_median', projectnumber),
      this.getAggregationReportAI('gender', projectnumber),
      this.getAggregationReportAI('changedjobs', projectnumber),

    ]).subscribe(res => this.aiSourcingHandlerService.emitAIStatsObject(res));
  }

  getChangedJobStats(start, end, changedJobStatus){
    const obj = {
      endDuration: end,
      changedjobsstatus: 'yes' // For aggregate type: changed jobs, then value of changedjobs is always true
    }
    this.getAggregationReport("changedjobs", obj).subscribe(res => this.changedJobStats(res));
  }

  private getAggregationReport(aggregatetype: string, obj?: any ) {
    // const localRequestObject = this.aggReqObjHandler.getSharedRequestObject();
    const localRequestObject = this.getRequestWithFilters();
    Object.assign(localRequestObject, {
      resultsize: 1,
      aggregatetype: aggregatetype
    });
    if ((aggregatetype === "changedjobs") && obj && !_.isEmpty(obj)) {
      Object.assign(localRequestObject, {
        // changedjobsstartdurationinmonths: obj?.statrDuration,
        changedjobsenddurationinmonths: obj?.endDuration,
      });
    }
    if ((aggregatetype === "changedjobs")) {
      Object.assign(localRequestObject, {
        // changedjobsstartdurationinmonths: obj?.statrDuration,
        changedjobs: 'yes'
      });
    }

    if(aggregatetype == 'school' || aggregatetype == 'education' || aggregatetype == 'fieldofstudy' || aggregatetype == 'school_location') {
      Object.assign(localRequestObject, {
        considerfilterforaggregates: true
      });
   }
    delete localRequestObject["talentdiscoveryhistoryid"];
    const type = this.route.snapshot.queryParams['type'];
    const searchSource = this.route.snapshot.queryParams['searchSource'];
    const searchKeyword = this.route.snapshot.queryParams['keyword'];

    if(this.similarCandidateService?.isListingCalledFromSimilarCandidate || type == 'quick-search' || this.router.url.includes('person/search') || ((searchSource && searchSource == 'history-component') && searchKeyword))
      localRequestObject["calledfrom"] = "listing";
    else
      delete localRequestObject["calledfrom"]
    delete localRequestObject["input"];
    delete localRequestObject["typeahead"];
    this.personSearchAggSub && this.personSearchAggSub.unsubscribe();
    // this.personSearchAggSub = this.personSearchService.getTotalAggs(localRequestObject).subscribe((response) => {});
    if (this.aiSourcingService.isAISourcingTab) {
      const projectnumber = this.aiSourcingService.isAISourcingprojectNumber;
      return this.aiSourcingService.getAISourcingFilterAgg(projectnumber, localRequestObject);
    } else {
      return this.personSearchService.getTotalAggs(localRequestObject);
    }


    // this.personSearchAggSub && this.personSearchAggSub.unsubscribe();

    // return new Observable((observer) => {
    //   this.personSearchAggSub = this.personSearchService.getTotalAggs(localRequestObject).subscribe({
    //     next: (response) => {
    //       observer.next(response);
    //       observer.complete();
    //     },
    //     error: (error) => {
    //       observer.error(error);
    //     }
    //   });
    // });

  }

  personSearchSub: Subscription = null;
  /**
   *
   *  Before calling this method make sure to update the filterData object from filter component or any other component that might be calling this method if applicable.
   *  Once we get the response, we will call the getTotalAggs if insights tab is active.
   *
   */
  getPersonSearchRecommendedCandidates(extraInfo : ExtraInfo) {
    this.loaderEventService.emitLoaderObject(LoaderType.RECOMMENDED_CANDIDATE);
    const request = this.getRequestWithFilters(extraInfo);
    delete request.aggregatetype;
    this.aggReqObjHandler.setShareRequestObject(request);

    /** profiles per page selection for person-search-listing */
    const preferenceArray = this.localStorageService.getItem('userdata').preferenceData;
    preferenceArray && preferenceArray.length && preferenceArray.map(item => {
      if (item.listing_type == 'person-search' && item.preference_type == 'page-size') {
        request['resultsize'] = item.preference_value;
      }
    });
    request['resultsize'] = request['resultsize'] ? request['resultsize'] : 25;

    this.modifyExperienceFilterData(request);
    this.personSearchSub && this.personSearchSub.unsubscribe();
    this.personSearchSub = this.personSearchService.getPersonSearchRecommendCandidates(request).subscribe(response => {
      const candidateObject = new PersonSearchResponse();
      if (response.status == 'success') {
        candidateObject.candidateList = response?.data?.results?.length ? response.data.results.map(c => ({ ...c, selected: false })) : [];
        candidateObject.totalCandidatesCount = response.data?.totalresultscount;
        candidateObject.talentdiscoveryhistoryid = response?.data?.talentdiscoveryhistoryid;
        candidateObject.totalresultscount = response?.data?.totalresultscount;
        candidateObject.booleanquery = response?.data?.booleanquery;
        candidateObject.message = response?.message;
      }
      this.emitRecCandidateObject(candidateObject);
    });
  }

  /**Have to set values to default range of 0-40 If any of the value from Or to Exist */
  modifyExperienceFilterData(request){
    request.toyearsofexperience = _.flatten(request?.toyearsofexperience);
    request.fromyearsofexperience = _.flatten(request?.fromyearsofexperience);
    request.currenttoyearsofexperience = _.flatten(request?.currenttoyearsofexperience);
    request.currentfromyearsofexperience = _.flatten(request?.currentfromyearsofexperience);

    if (!(request?.toyearsofexperience?.length && request?.fromyearsofexperience?.length) &&
      (request?.toyearsofexperience?.length || request?.fromyearsofexperience?.length)) {
      if (!request?.toyearsofexperience?.length) {
        request.toyearsofexperience = [40];
      } else if (!request?.fromyearsofexperience?.length) {
        request.fromyearsofexperience = [0];
      }
    }

    if (!(request?.currenttoyearsofexperience?.length && request?.currentfromyearsofexperience?.length) &&
      (request?.currenttoyearsofexperience?.length || request?.currentfromyearsofexperience?.length)) {
      if (!request?.currenttoyearsofexperience?.length) {
        request.currenttoyearsofexperience = [40];
      } else if (!request?.currentfromyearsofexperience?.length) {
        request.currentfromyearsofexperience = [0];
      }
    }
  }



  /**
   *
   * This method will return redirect data if redirect from talent or company insights
   *
   */
  getRedirectDataV2() : ExtraInfo {
    const extraInfo = new ExtraInfo();
    extraInfo.redirectSource = this.activatedRoute.snapshot.queryParams['redirectSource'];
    extraInfo.redirectData = extraInfo.redirectSource == 'talent-insights' ? this.getTalentInsightsMandatoryData(): this.getCompanyInsightsMandatoryData();
    if (extraInfo?.redirectData?.additionalFilters) {
      Object.keys(extraInfo.redirectData.additionalFilters).forEach(filter => {
        Object.assign(extraInfo.redirectData, { [filter]: extraInfo.redirectData.additionalFilters[filter] });
      });
    }
    return extraInfo;
  }


  getRedirectData() : ExtraInfo {
    const extraInfo = new ExtraInfo();
    extraInfo.redirectSource = this.activatedRoute.snapshot.queryParams['redirectSource'];

    const talentFilters = this.localStorageService.getItem('talentfilters');
    const companyFilters = this.localStorageService.getItem('companyinsightsdetails');
    if (extraInfo.redirectSource === "talent-insights" && !!talentFilters) {
      extraInfo.redirectData = JSON.parse(talentFilters);
    } else if (extraInfo.redirectSource === "company-insights" && !!companyFilters) {
      extraInfo.redirectData = JSON.parse(companyFilters);
    } else {
      extraInfo.redirectData = null;
    }
    if (extraInfo?.redirectData?.additionalFilters) {
      Object.keys(extraInfo.redirectData.additionalFilters).forEach(filter => {
        Object.assign(extraInfo.redirectData, { [filter]: extraInfo.redirectData.additionalFilters[filter] });
      });
    }

    return extraInfo;
  }

  /**
   *
   * TODO : pick all status map data from filter data object. if time permits remove status map param
   *
   * @param extraInfo
   * @param statusMap
   * @returns {AggregationRequest}
   */
  getRequestWithFilters(extraInfo?: ExtraInfo) {
    const request = this.aggReqObjHandler.getSharedRequestObject();
    /**For now company ids will be used in company insights */
    request.companyids = [];
    let filterData = this.filterDataHandlerService.fetchFilterData;
    const statusMap = this.getStatusMap(filterData);
    if (!extraInfo) extraInfo = (this.filterDataHandlerService.isRedirectFromTalentInsightsPage || this.filterDataHandlerService.isRedirectFromCompanyInsightsPage) ? this.getRedirectDataV2() : this.getRedirectData();
    const redirectDataTI = extraInfo?.redirectSource == 'talent-insights' ? this.getTalentInsightsRedirectDataV2(): this.getCompanyInsightsRedirectDataV2();
    const jdFilterData = extraInfo.calledFrom === GeneralConstants.COMPONENT_NAME.SC_MODAL ?
      extraInfo.redirectData :
      this.jdDataService.jdDataFromSession;

    this.applyFilters(request, filterData, extraInfo, redirectDataTI, statusMap);

    request.profilehighlights = filterData.profilehighlights
    request.positionhierarchies = filterData.positionhierarchy;

    // history id
    request.talentdiscoveryhistoryid = filterData.talentdiscoveryhistoryid;
    request.savehistory = filterData.savehistory;

    if(this.talentInsightsReqHandlerService.isTalentInsightsTab) {
      if(this.jdDataService.isComingFromJobDescription || this.jdDataService.isComingFromNLSearch){
          request.continentstobeincluded = (filterData?.continentstobeincluded?.length) ? filterData.continentstobeincluded: jdFilterData?.locationcontinent?.length > 0 ? jdFilterData.locationcontinent : [this.localStorageService.getItem('defaultContinent')];
         request.continentstobeincluded = _.uniq(_.flatten(request.continentstobeincluded));
    if(request?.job_description_keys?.locations?.length == 0) {
        request.job_description_keys.locations = [this.localStorageService.getItem('defaultContinent')]
      }
        if(this.route.snapshot.queryParams['searchSource'] == 'history-component') {
          request.continentstobeincluded = filterData.continentstobeincluded.length ? filterData.continentstobeincluded : request?.continentstobeincluded?.length ? request.continentstobeincluded : [];
          request.continentstobeincluded = request.continentstobeincluded?.length ? request.continentstobeincluded.filter(x=>x!= null && x!= undefined && x!= '') : [];
        request.continentstobeincluded = _.uniq(request.continentstobeincluded)
        }
      } else {
  if(this.route.snapshot.queryParams['searchSource'] == 'history-component') {
        request.continentstobeincluded = (filterData?.continentstobeincluded?.length) ? filterData.continentstobeincluded : [];
        request.continentstobeincluded = request.continentstobeincluded?.length ? request.continentstobeincluded.filter(x=>x!= null && x!= undefined && x!= '') : [];
        request.continentstobeincluded = _.uniq(request.continentstobeincluded)
      } else {

        request.continentstobeincluded = (filterData?.continentstobeincluded?.length) ? filterData.continentstobeincluded : request?.exclude_locations?.length ? []:[this.localStorageService.getItem('defaultContinent')];
        request.continentstobeincluded = request.continentstobeincluded?.length ? request.continentstobeincluded.filter(x=>x!= null && x!= undefined && x!= '') : [];
        request.continentstobeincluded = _.uniq(request.continentstobeincluded)
      }
      }
      delete request.module;
      request.locationcontinents = request?.locationcontinents?.length > 0 ? request?.locationcontinents: filterData?.locationcontinents?.length > 0 ? filterData?.locationcontinents:[]
    } else if (this.jdDataService.isComingFromJobDescription) {
      request.continentstobeincluded = filterData?.continentstobeincluded?.length > 0 ? filterData?.continentstobeincluded: jdFilterData?.locationcontinent?.length ? jdFilterData.locationcontinent : [this.localStorageService.getItem('defaultContinent')];
      request.continentstobeincluded = _.uniq(_.flatten(request.continentstobeincluded));
        if(request?.job_description_keys?.locations?.length == 0 && request?.job_description_keys?.mandatory_locations?.length == 0) {
        request.job_description_keys.locations = [this.localStorageService.getItem('defaultContinent')]
      }
        if(this.route.snapshot.queryParams['searchSource'] == 'history-component') {
          request.continentstobeincluded = filterData.continentstobeincluded.length ? filterData.continentstobeincluded : request?.continentstobeincluded?.length ? request.continentstobeincluded : [];
        }
    } else if (this.similarCandidateService.isListingCalledFromSimilarCandidate){
      request.continentstobeincluded = filterData.continentstobeincluded.length ? filterData.continentstobeincluded : request.continentstobeincluded;
      request.continentstobeincluded = _.uniq(_.flatten(request.continentstobeincluded));


       if(this.route.snapshot.queryParams['searchSource'] == 'history-component') {
          request.continentstobeincluded = filterData.continentstobeincluded.length ? filterData.continentstobeincluded : [];
        }

    } else if(this.jdDataService.isComingFromNLSearch) {
      request.continentstobeincluded = filterData?.continentstobeincluded?.length > 0 ? filterData?.continentstobeincluded: jdFilterData?.locationcontinent?.length ? jdFilterData.locationcontinent : [this.localStorageService.getItem('defaultContinent')];
      request.continentstobeincluded = _.uniq(_.flatten(request.continentstobeincluded));
          if(request?.job_description_keys?.locations?.length == 0 && request?.job_description_keys?.mandatory_locations?.length == 0) {
        request.job_description_keys.locations = [this.localStorageService.getItem('defaultContinent')]
      }
        if(this.route.snapshot.queryParams['searchSource'] == 'history-component') {
          request.continentstobeincluded = filterData?.continentstobeincluded?.length ? filterData?.continentstobeincluded : [];
        }
    }

    else {
      if(this.route.snapshot.queryParams['searchSource'] == 'history-component') {
        request.continentstobeincluded = filterData?.continentstobeincluded?.length ? filterData?.continentstobeincluded: [];
      } else {
        request.continentstobeincluded = filterData?.continentstobeincluded?.length ? filterData?.continentstobeincluded: request?.exclude_locations?.length ? []: [this.localStorageService.getItem('defaultContinent')];
      }
    }
    if(this.talentInsightsReqHandlerService.isCompanyInsightsTab || this.talentInsightsReqHandlerService.isCompanyCompareTab || extraInfo.redirectSource == 'company-insights'){
      request.continentstobeincluded = [];
    }
    request.locationcontinents = filterData.locationcontinents;
    if(this.jdDataService.isComingFromJobDescription){  // In case of jd search location is not picked from IP
      if(filterData.locationSelectedList && filterData.locationSelectedList.length){
        request.locations = filterData.locationSelectedList.filter(l => l.isExcluded == false).map(l => l.key);
      } else{
        request.locations = [];
      }
    }
    else if(filterData.locationSelectedList && filterData.locationSelectedList.length){
      request.locations = filterData.locationSelectedList.filter(l => l.isExcluded == false).map(l => l.key);
    } else{
      if(this.geoIpLocation && request.continentstobeincluded && request.continentstobeincluded.length && this.geoIpLocation.continent == request.continentstobeincluded[0] && !this.aiSourcingService.isAISourcingTab && !this.talentInsightsReqHandlerService.isCompanyInsightsTab && !this.talentInsightsReqHandlerService.isCompanyCompareTab && !this.similarCandidateService.isListingCalledFromSimilarCandidate && !this.jdDataService.isComingFromJobDescription && !this.jdDataService.isComingFromNLSearch){
        request.locations = [`${this.geoIpLocation.location}.iplocation`];
      } else{
         if(!this.jdDataService.isComingFromJobDescription && !this.jdDataService.isComingFromNLSearch && !this.similarCandidateService.isListingCalledFromSimilarCandidate && extraInfo.redirectSource != 'talent-insights' && extraInfo.redirectSource != 'company-insights' && !this.talentInsightsReqHandlerService.isCompanyInsightsTab && !this.talentInsightsReqHandlerService.isCompanyCompareTab && !this.aiSourcingService.isAISourcingTab) {

        request.locations = [this.localStorageService.getItem('defaultContinent')];
      } else {
        request.locations = [];
      }
    }
  }

    // request.locations = filterData.locationSelectedList && filterData.locationSelectedList.length ? filterData.locationSelectedList.filter(l => l.isExcluded == false).map(l => l.key) : this.geoIpLocation && request.continentstobeincluded && request.continentstobeincluded.length && this.geoIpLocation.continent == request.continentstobeincluded[0] ? [`${this.geoIpLocation.location}.iplocation`] : [];

    // Handling all the mandatory fields
    this.applyMandatoryFilters(request, extraInfo, redirectDataTI, filterData);

    // Handling all the request insights filters
    // Replace new FilterParams with company insights redirect data later
    this.applyInsightsFilters(request, extraInfo, redirectDataTI);

    if (
      (this.filterDataHandlerService.isRedirectFromTalentInsightsPage ||
      this.filterDataHandlerService.isReportDetailsPage ||
      this.filterDataHandlerService.isRedirectFromCompanyInsightsPage) &&
      !!request.calledfrom
    ) {
      delete request.calledfrom;
    }

    this.filterDataHandlerService.isPersonSearchListingPage && (request.needcuratedtalentpooltags = true);

    const searchType = this.activatedRoute.snapshot.queryParams['type'];
    if(searchType == 'quick-search') {
      request.calledfrom = 'listing';
      request.startindex = 0;
      request.resultsize = 25;
      request.searchviaboolean =  true;
    }

    if (this.jdDataService.isComingFromJobDescription) {
      request.searchedfrom = GeneralConstants.REDIRECT_SOURCES.JOB_DESCRIPTION;
      request.job_description_keys = this.jdDataService.getJdKeys(jdFilterData);
      request.jdsearchhistoryid = this.localStorageService.getItem('jdinsightshistoryid');
       request.nlsearchquery = filterData?.nlsearchquery ? filterData.nlsearchquery : this.localStorageService.getItem('nlSearchKeyword');
        if(request?.job_description_keys?.locations?.length == 0) {
        request.job_description_keys.locations = [this.localStorageService.getItem('defaultContinent')]
      }
       delete request.module;
    }

    if(this.aiSourcingService.isAISourcingInsightsTab) {
      request.job_description_keys = this.jdDataService.getJdKeys(jdFilterData);
    }

    if(this.jdDataService.isComingFromNLSearch) {
         request.searchedfrom = GeneralConstants.REDIRECT_SOURCES.JOB_DESCRIPTION;
      request.job_description_keys = this.jdDataService.getJdKeys(jdFilterData);
      request.nlsearchquery = filterData?.nlsearchquery ? filterData.nlsearchquery : this.localStorageService.getItem('nlSearchKeyword');
       if(request?.job_description_keys?.locations?.length == 0) {
        request.job_description_keys.locations = [this.localStorageService.getItem('defaultContinent')]
      }
    }
    if (extraInfo?.redirectData?.comingFromSC) {
      this.similarCandidateService.modifyRequestForSCCount(request, extraInfo?.redirectData);
      request.continentstobeincluded = request.continentstobeincluded;
    }
    if(this.filterDataHandlerService.isCalledFromPersonSearch()) {
      request.module = 'personsearch';
    } else {
      delete request.module;
    }
    request.job_search_keys = request?.job_search_keys;

    return request;
  }

  private applyInsightsFilters(request: AggregationRequest, extraInfo: ExtraInfo, redirectDataTI: FilterParams) {
    if (extraInfo?.redirectData?.comingFromSC)
      return;

    request.insightslocations = this.getInsightsLocation(extraInfo, redirectDataTI, new FilterParams());
    request.insightscompanies = this.getInsightsCompanies(extraInfo, redirectDataTI, new FilterParams());
    request.insightsindustries = this.getInsightsIndustries(extraInfo, redirectDataTI, new FilterParams());
    request.insightsschools = this.getInsightsSchools(extraInfo, redirectDataTI, new FilterParams());
    request.insightseducationalmajors = this.getInsightsEducationalMajors(extraInfo, redirectDataTI, new FilterParams());
    request.insightsjobtitles = this.getInsightsJobTitles(extraInfo, redirectDataTI, new FilterParams());
    request.insightspastjobtitles = this.getInsightsPastJobTitles(extraInfo, redirectDataTI, new FilterParams());
    request.insightspreferredskills = this.getInsightsPrefferdedSkills(extraInfo, redirectDataTI, new FilterParams());
    request.insightsfunctions = this.getInsightsFunctions(extraInfo, redirectDataTI, new FilterParams());
    request.insightseducationaldegrees = this.getInsightsEducationalDegrees(extraInfo, redirectDataTI, new FilterParams());
    request.insightsseniority = this.getInsightsSeniority(extraInfo, redirectDataTI, new FilterParams());
    request.lastjobcompanies = this.getLastJobCompanies(extraInfo, redirectDataTI, new FilterParams());
    request.insightscompaniesid = this.getCompanyId(extraInfo, redirectDataTI, new FilterParams());

    request.lastjobcompanyids = this.getLastJobCompanyId(extraInfo, redirectDataTI, new FilterParams());
    request.durationinmonths = this.getRecentHireAndAttrition(extraInfo);

    if (this.manadatoryApplicable(extraInfo) && extraInfo.redirectData) {
      request.recentgraduatesonly = extraInfo.redirectData?.recentgraduatesonly;
      request.recentcompanyhires = extraInfo.redirectData?.recentcompanyhires;
    }

    if (extraInfo.redirectSource === 'talent-insights' && extraInfo.redirectData) {
      request.recentcompanyattrition = extraInfo.redirectData?.recentcompanyattrition;
    }
  }

  private applyMandatoryFilters(request: AggregationRequest, extraInfo: ExtraInfo, redirectDataTI: FilterParams, filterData: FilterParams) {
    if (extraInfo?.redirectData?.comingFromSC)
      return;

    request.mandatoryskills = this.getMandatorySkills(extraInfo, redirectDataTI, new FilterParams(), filterData);
    request.mandatoryjobtitles = this.getMandatoryJobTitle(extraInfo, redirectDataTI, new FilterParams(), filterData);
    this.manadatoryApplicable(extraInfo) && extraInfo?.redirectData?.mandatoryjobtitles?.length ?
      filterData?.jobTitleSelectedList?.filter(s => !!s.isMandatory).map(s => s.key).concat(extraInfo?.redirectData?.mandatoryjobtitles) :
      filterData?.jobTitleSelectedList?.filter(s => !!s.isMandatory).map(s => s.key);
    request.mandatorylocations = this.manadatoryApplicable(extraInfo) && this.getMandatoryLocations(extraInfo);
    request.mandatorycompanies = this.manadatoryApplicable(extraInfo) && this.getMandatoryCompanies(extraInfo);
    request.mandatoryindustries = this.manadatoryApplicable(extraInfo) && this.getMandatoryIndustries(extraInfo);
    request.mandatoryschools = this.manadatoryApplicable(extraInfo) && this.getMandatorySchools(extraInfo);;
    request.mandatoryfunctions = this.manadatoryApplicable(extraInfo) && extraInfo?.redirectData?.mandatoryfunctions?.length ? extraInfo?.redirectData?.mandatoryfunctions : [];
    request.mandatoryeducationalmajors = this.manadatoryApplicable(extraInfo)
      && this.getMandatoryEducationalMajors(extraInfo);
    request.mandatoryeducationaldegrees = this.manadatoryApplicable(extraInfo)
      && this.getMandatoryEducationalDegrees(extraInfo);
    request.mandatoryseniority = this.manadatoryApplicable(extraInfo)
      && extraInfo?.redirectData?.mandatoryseniority?.length ? extraInfo?.redirectData?.mandatoryseniority : [];
    request.exclude_mandatoryskills = this.getExcludeMandatorySkills(extraInfo, redirectDataTI, new FilterParams(), filterData);
  }

  private applyFilters(request: AggregationRequest, filterData: FilterParams, extraInfo: ExtraInfo, redirectDataTI: FilterParams, statusMap: { jobTitleStatus: string; companyStatus: string; industryStatus: string; yearsOfExperienceType: any; }) {
    if (extraInfo?.redirectData?.comingFromSC)
      return;

    request.matchcategory = filterData?.overallMatchFilters ? _.flatten(filterData?.overallMatchFilters) : [];

    request.booleanquery = this.getBooleanQuery(extraInfo, redirectDataTI, new FilterParams(), filterData);

    request.jobtitles = _.uniq(filterData?.jobTitleSelectedList?.filter(jobTitle => !jobTitle.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key));
    request.exclude_jobtitles = this.getExcludeJobtitles(extraInfo, redirectDataTI, new FilterParams(), filterData);
    request.jobtitletype = statusMap.jobTitleStatus;

    request.exclude_locations = this.getExcludeLocations(extraInfo, redirectDataTI, new FilterParams(), filterData);

    request.pastjobtitles = filterData?.pastJobTitleSelectedList?.filter(l => !l.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key);
    request.exclude_pastjobtitles = this.getExcludePastJobTitles(extraInfo, redirectDataTI, new FilterParams(), filterData);

    request.preferredskills = filterData?.skillsSelectedList?.filter(s => (!s.isMandatory && !s.isExcluded)).map(s => s.isSuggested && !s.key.includes('.aisearch') ? `${s.key}.aisearch` : s.key);

    request.companies = filterData?.companySelectedList?.filter(company => company.isExcluded == false).map(j => j.key);
    request.exclude_companies = this.getExcludeCompanies(extraInfo, redirectDataTI, new FilterParams(), filterData);
    request.companytype = this.getCompanyStatus(extraInfo, filterData);

    request.companysizes = this.getCompanySizes(extraInfo, redirectDataTI, new FilterParams(), filterData);

    request.profilehighlights = filterData.profilehighlights;

    request.industries = filterData?.industrySelectedList?.filter(industry => !industry.isExcluded).map(i => i.key);
    request.industrytype = filterData.industryStatus == CallStage.CURRENT_OR_PAST.toString() ? 'All' : filterData.industryStatus;
    request.exclude_industries = this.getExcludeIndustries(extraInfo, redirectDataTI, new FilterParams(), filterData);
    request.functions = filterData?.functionSelectedList?.filter(l => !l.isExcluded).map(l => l.key);
    request.functiontype = this.getFunctionStatusForTI(extraInfo, filterData);
    request.seniority = filterData?.seniority ? _.flatten(filterData?.seniority) : [];

    // Years of experience
    request.fromyearsofexperience = this.getFromYrsOfExperience(extraInfo, redirectDataTI, new FilterParams(), filterData);
    request.toyearsofexperience = this.getToYrsOfExperience(extraInfo, redirectDataTI, new FilterParams(), filterData);
    request.experiencetype =
      extraInfo?.redirectSource === 'talent-insights' && !filterData.filterAppliedOnYearsOfExperience
        ? redirectDataTI.yearsofexperience :
        filterData.yearsofexperience;

    request.currentfromyearsofexperience = this.getYrsInCurrentCompanyFrom(extraInfo, redirectDataTI, new FilterParams(), filterData);
    request.currenttoyearsofexperience =
      extraInfo?.redirectSource == 'talent-insights' && !filterData.currenttoyearsofexperience
        ? !!redirectDataTI.currenttoyearsofexperience ? [redirectDataTI.currenttoyearsofexperience] : [] :
        !!filterData.currenttoyearsofexperience ? [filterData.currenttoyearsofexperience] : [];

    // Gender
    request.genders = this.getGender(extraInfo, redirectDataTI, null, filterData);

    // Education
    request.educationaldegrees = filterData?.educationSelectedList?.map(e => e.key);

    // School
    request.schools = filterData?.schoolSelectedList?.map(s => s.key);

    // Education List
    request.schoollocation = filterData?.schoolLocationSelectedList?.filter(l => !l.isExcluded).map(l => l.key);

    // Exclude education location
    request.exclude_schoollocation = this.getExcludSchoolLocation(extraInfo, redirectDataTI, new FilterParams(), filterData);


    // Field Of Study
    request.educationalmajors = filterData?.fieldOfStudySelectedList?.map(fs => fs.key);

    // Likely To Move In
    request.likelytomovetype = filterData.candidateslikelytomove;

    // Changed jobs
    request.changedjobs = filterData.changedJobStatus;
    request.changedjobsstartdurationinmonths = filterData?.changedJobStartDuration;
    request.changedjobsenddurationinmonths = filterData?.changedJobEndDuration;

    request.fullnames = filterData.fullNameSelectedList?.length > 0 ? filterData.fullNameSelectedList.map(x=>x.key): [];

    if(!this.similarCandidateService.isListingCalledFromSimilarCandidate && !this.jdDataService.isComingFromJobDescription && !this.jdDataService.isComingFromNLSearch) {

      //If value of distancefromselectedlocation is null from filtersData then it, than it takes value of redirection filters and if it is not null than it use data from filters applied
      if((this.filterDataHandlerService.isRedirectFromTalentInsightsPage || this.filterDataHandlerService.isRedirectFromCompanyInsightsPage) && filterData.distancefromselectedlocations === null){
        request.distancefromselectedlocations = redirectDataTI?.distancefromselectedlocations;
        request.distancefromselectedlocationsmeasurementunit = redirectDataTI?.distancefromselectedlocationsmeasurementunit;
      }
      else{
        request.distancefromselectedlocations = filterData?.distancefromselectedlocations;
        request.distancefromselectedlocationsmeasurementunit = filterData?.distancefromselectedlocationsmeasurementunit;
      }


    request.toyearsofexperience = _.flatten(request?.toyearsofexperience);
    request.fromyearsofexperience = _.flatten(request?.fromyearsofexperience);
    request.currenttoyearsofexperience = _.flatten(request?.currenttoyearsofexperience);
    request.currentfromyearsofexperience = _.flatten(request?.currentfromyearsofexperience);

      if (!(request?.toyearsofexperience?.length && request?.fromyearsofexperience?.length) &&
      (request?.toyearsofexperience?.length || request?.fromyearsofexperience?.length)) {
      if (!request?.toyearsofexperience?.length) {
        request.toyearsofexperience = [40];
      } else if (!request?.fromyearsofexperience?.length) {
        request.fromyearsofexperience = [0];
      }
    }

    if (!(request?.currenttoyearsofexperience?.length && request?.currentfromyearsofexperience?.length) &&
      (request?.currenttoyearsofexperience?.length || request?.currentfromyearsofexperience?.length)) {
      if (!request?.currenttoyearsofexperience?.length) {
        request.currenttoyearsofexperience = [40];
      } else if (!request?.currentfromyearsofexperience?.length) {
        request.currentfromyearsofexperience = [0];
      }
    }
  }
  request.tograduationyear = this.getYearsOfGraduationKeys('tograduationyear', extraInfo, redirectDataTI, new FilterParams(), filterData)
  request.fromgraduationyear = this.getYearsOfGraduationKeys('fromgraduationyear', extraInfo, redirectDataTI, new FilterParams(), filterData);
  request.exclude_preferredskills = this.getExcludeSkills(extraInfo, redirectDataTI, new FilterParams(), filterData);
  request.exclude_functions = this.getExcludeFunctions(extraInfo, redirectDataTI, new FilterParams(), filterData)

}

  getYearsOfGraduationKeys(key, extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    if (key == 'tograduationyear') {
      if (((extraInfo?.redirectSource == 'talent-insights' || extraInfo?.redirectSource == 'company-insights') && !filterData.tograduationyear)) {
        return redirectDataTI?.tograduationyear;
      }
      return filterData.tograduationyear;
    } else if (key == 'fromgraduationyear') {
      if (((extraInfo?.redirectSource == 'talent-insights' || extraInfo?.redirectSource == 'company-insights') && !filterData.fromgraduationyear)) {
        return redirectDataTI?.fromgraduationyear;
      }
      return filterData.fromgraduationyear;
    }
    return null
  }

  private getFromYrsOfExperience(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    if (extraInfo?.redirectSource === 'talent-insights' && !filterData.yearsofexperienceFrom && redirectDataTI?.yearsofexperienceFrom)
      return [redirectDataTI.yearsofexperienceFrom];

    //It should be valid for Zero, Gives falsy value only in case of null
    if (typeof(filterData.yearsofexperienceFrom) == 'number')
      return [filterData.yearsofexperienceFrom];

    return [];
  }

  private getToYrsOfExperience(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    if (extraInfo?.redirectSource === 'talent-insights' && !filterData.yearsofexperienceTo && redirectDataTI?.yearsofexperienceTo)
      return [redirectDataTI.yearsofexperienceTo];

    //It should be valid for Zero
    if (typeof(filterData.yearsofexperienceTo) == 'number')
      return [filterData.yearsofexperienceTo];

    return [];
  }

  private getYrsInCurrentCompanyFrom(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {

    if(extraInfo?.redirectSource == 'talent-insights' && !filterData.currentfromyearsofexperience){
      if(!!redirectDataTI.currentfromyearsofexperience || typeof(redirectDataTI.currentfromyearsofexperience) == 'number'){
        return [redirectDataTI.currentfromyearsofexperience];
      }
      else{
        return [];
      }
    }
    else{
      if(!!filterData.currentfromyearsofexperience || typeof(filterData.currentfromyearsofexperience) == 'number'){
        return [filterData.currentfromyearsofexperience]
      }
      else return [];
    }
  }

  private getCompanyStatus(extraInfo: ExtraInfo, filterData: FilterParams) {
    if (extraInfo?.redirectSource === 'talent-insights' && extraInfo?.redirectData?.recentcompanyattrition)
      return CallStage.PAST.toString();

    if (extraInfo?.redirectSource === 'talent-insights' && extraInfo?.redirectData?.recentcompanyhires)
      return CallStage.CURRENT.toString();

    if (extraInfo.redirectSource == 'company-insights') {
      if (extraInfo.redirectData.recentAttrtion) {
        return CallStage.PAST;
      } else {
        return CallStage.CURRENT;
      }
    }
    return filterData.companyStatus == CallStage.CURRENT_OR_PAST.toString() ? "All" : filterData.companyStatus;
  }

  private getFunctionStatusForTI(extraInfo: ExtraInfo, filterData: FilterParams) {
    if (extraInfo?.redirectSource === 'company-insights' || extraInfo?.redirectSource === 'talent-insights' || this.talentInsightsReqHandlerService.isCompanyInsightsTab || this.talentInsightsReqHandlerService.isCompanyCompareTab)
      return CallStage.CURRENT.toString();
    if(this.talentInsightsReqHandlerService.isTalentInsightsTab)
      return CallStage.CURRENT.toString();
    if(this.talentInsightsReqHandlerService.isTalentInsightsTab)
      return CallStage.CURRENT.toString();
    else
      return 'All';


 //   return filterData.functionStatus == CallStage.CURRENT_OR_PAST.toString() ? "All" : filterData.functionStatus;
  }

  private getMandatoryJobTitle(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let manadatoryJobTitles = [];
    if (filterData.jobTitleSelectedList?.length) {
      manadatoryJobTitles = filterData.jobTitleSelectedList?.filter(el => !!el.isMandatory).map(el => el.key);
    }
    if (this.manadatoryApplicable(extraInfo) && extraInfo?.redirectData?.mandatoryjobtitles?.length) {
      manadatoryJobTitles = manadatoryJobTitles.concat(extraInfo?.redirectData?.mandatoryjobtitles);
    }
    if ("talent-insights" === extraInfo.redirectSource && redirectDataTI.jobTitleSelectedList?.length) {
      manadatoryJobTitles = manadatoryJobTitles.concat(redirectDataTI.jobTitleSelectedList?.filter(el => !!el.isMandatory).map(el => el.key));
    }

    return manadatoryJobTitles;
  }

  private getMandatorySkills(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let mandatorySkills = [];
    if (filterData.skillsSelectedList?.length) {
      mandatorySkills = filterData.skillsSelectedList?.filter(el => (!!el.isMandatory && !el.isExcluded)).map(el => el.isSuggested && !el.key.includes('.aisearch') ? `${el.key}.aisearch` : el.key);
    }
    if (this.manadatoryApplicable(extraInfo) && extraInfo?.redirectData?.mandatoryskills?.length) {
      mandatorySkills = mandatorySkills.concat(extraInfo?.redirectData?.mandatoryskills);
    }
    if (("talent-insights" === extraInfo.redirectSource || "company-insights" === extraInfo.redirectSource) && redirectDataTI.skillsSelectedList?.length) {
      mandatorySkills = mandatorySkills.concat(redirectDataTI.skillsSelectedList?.filter(el => (!!el.isMandatory && !el.isExcluded)).map(el => el.key.isSuggested && !el.includes('.aisearch') ? `${el.key}.aisearch` : el.key));
    }

    return mandatorySkills;
  }

  private getMandatoryLocations(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatorylocations?.length)
      return extraInfo?.redirectData?.mandatorylocations;

    return [];
  }

  private getMandatoryCompanies(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatorycompanies?.length)
      return extraInfo?.redirectData?.mandatorycompanies;

    return [];
  }

  private getMandatoryIndustries(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatoryindustries?.length)
      return extraInfo?.redirectData?.mandatoryindustries;

    return [];
  }

  private getMandatorySchools(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatoryschools?.length)
      return extraInfo?.redirectData?.mandatoryschools;

    return [];
  }

  private getMandatoryEducationalMajors(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatoryeducationalmajors?.length)
      return extraInfo?.redirectData?.mandatoryeducationalmajors;

    return [];
  }

  private getMandatoryEducationalDegrees(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatoryeducationaldegrees?.length)
      return extraInfo?.redirectData?.mandatoryeducationalmajors;

    return [];
  }

  private isComingFromInsights(extraInfo: ExtraInfo) {
    return (
      extraInfo?.redirectSource == 'talent-insights' ||
      extraInfo?.redirectSource == 'company-insights'
    );
  }

  // TODO : Use redirectDataCI when working on Company Insights
  private getInsightsLocation(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights'|| extraInfo.redirectSource === 'company-insights') && redirectDataTI?.locationSelectedList?.length)
      return redirectDataTI?.locationSelectedList?.filter(el => !el.isExcluded).map(el => el.key);

    // if (extraInfo?.redirectSource === 'company-insights' && extraInfo?.redirectData?.locations?.length)
    //   return extraInfo?.redirectData?.locations;

    return [];
  }

  private getInsightsCompanies(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if (extraInfo.redirectSource === 'talent-insights' && redirectDataTI?.companySelectedList?.length)
      return redirectDataTI?.companySelectedList?.filter(el => !el.isExcluded).map(el => el.key);

    if (extraInfo?.redirectSource === 'company-insights' && extraInfo?.redirectData?.losingtalent)
      return extraInfo?.redirectData?.losingtalent;

    if (extraInfo?.redirectSource === 'company-insights' && extraInfo?.redirectData?.alternative_names)
      return extraInfo?.redirectData?.alternative_names.concat(extraInfo?.redirectData?.name);

    if (extraInfo?.redirectSource === 'company-insights' && !extraInfo?.redirectData?.alternative_names)
      return [extraInfo?.redirectData?.name];

    return [];
  }

  private getInsightsIndustries(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.industrySelectedList?.length)
      return redirectDataTI?.industrySelectedList?.filter(el => !el.isExcluded).map(el => el.key);

    return [];
  }

  private getInsightsSchools(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.schoolSelectedList?.length)
      return redirectDataTI?.schoolSelectedList?.map(el => el.key);

    return [];
  }

  private getInsightsEducationalMajors(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.fieldOfStudySelectedList?.length)
      return redirectDataTI?.fieldOfStudySelectedList?.map(el => el.key);

    return [];
  }

  private getInsightsJobTitles(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.jobTitleSelectedList?.length)
      return redirectDataTI?.jobTitleSelectedList?.filter(el => !el.isExcluded).map(el => el.key);

    return [];
  }

  private getInsightsPastJobTitles(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if (extraInfo.redirectSource === 'talent-insights' && redirectDataTI?.pastJobTitleSelectedList?.length)
      return redirectDataTI?.pastJobTitleSelectedList?.filter(el => !el.isExcluded).map(el => el.key);

    return [];
  }

  private getInsightsPrefferdedSkills(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
   // console.log("preferred", redirectDataTI.skillsSelectedList);
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.skillsSelectedList?.length)
    {

      return redirectDataTI?.skillsSelectedList?.filter(el => (!el.isMandatory && !el.isExcluded)).map(el => el.key);

    }


    // if (extraInfo.redirectSource === 'company-insights' && extraInfo?.redirectData?.skills?.length)
    //   return extraInfo?.redirectData?.skills;

    return [];
  }

  private getInsightsFunctions(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.functionSelectedList?.length)
      return redirectDataTI?.functionSelectedList?.filter(el => !el.isExcluded).map(el => el.key);

    // if (extraInfo.redirectSource === 'company-insights' && extraInfo?.redirectData?.functions?.length)
    //   return extraInfo?.redirectData?.functions.filter(Boolean);

    return [];
  }

  private getInsightsEducationalDegrees(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.educationSelectedList?.length)
      return redirectDataTI?.educationSelectedList?.map(el => el.key);

    return [];
  }

  private getInsightsSeniority(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.seniority?.length)
      return redirectDataTI?.seniority;

    return [];
  }

  private getLastJobCompanies(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if (extraInfo.redirectSource === 'company-insights' && extraInfo?.redirectData?.winningtalent)
      return extraInfo?.redirectData?.winningtalent;

    if (
      extraInfo.redirectSource === 'company-insights' &&
      extraInfo?.redirectData?.losingtalent &&
      extraInfo?.redirectData?.alternative_names
    )
      return extraInfo?.redirectData?.alternative_names?.concat(extraInfo?.redirectData?.name);

    if (
      extraInfo.redirectSource === 'company-insights' &&
      extraInfo?.redirectData?.losingtalent &&
      !extraInfo?.redirectData?.alternative_names
    )
      return [extraInfo.redirectData?.name];

    return [];
  }

  /** Handling company id key which needs to be sent in company insights */
  private getCompanyId(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams){
    /**In case of losingtalent redirection current company id will send to lastjobcompanyids and insightscompaniesid will be empty for now */
    if(extraInfo?.redirectSource === 'company-insights' && extraInfo.redirectData.losingtalent){
      return [];
    } else if (extraInfo?.redirectSource === 'company-insights'){
      return extraInfo?.redirectData['alternative_company_id'] || [];
    } else{
      return [];
    }
  }

  private getLastJobCompanyId(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if (extraInfo?.redirectSource == 'company-insights' && extraInfo?.redirectData?.recentAttrtion) {
      return extraInfo.redirectData.lastjobcompanyids || [];
    }
    return [];
  }

  /**
   * Get Duration for recent hire and attrition
   * @param extraInfo
   * @returns
   */
  private getRecentHireAndAttrition(extraInfo: ExtraInfo){
    if(extraInfo?.redirectSource == 'company-insights' && (extraInfo?.redirectData?.recentAttrtion || extraInfo?.redirectData?.recentGrowth)){
      return extraInfo.redirectData.durationinmonths
    }
    return null
  }

  private getGender(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    if (
      (extraInfo?.redirectSource === "talent-insights" || extraInfo?.redirectSource === "company-insights") &&
      ["male", "female"].includes(redirectDataTI?.gender?.toLowerCase())
    )
      return [redirectDataTI.gender];
    return filterData?.gender ? [filterData.gender] : [];
  }

  private getExcludeJobtitles(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let excludeJobTitles = filterData.jobTitleSelectedList?.length ?
      filterData.jobTitleSelectedList?.filter(j => !!j.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key) : [];
    if (("talent-insights" === extraInfo?.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.jobTitleSelectedList?.length)
      excludeJobTitles =  excludeJobTitles.concat(redirectDataTI.jobTitleSelectedList.filter(j => !!j.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key));
    return _.uniq(excludeJobTitles);
  }

  private getExcludeLocations(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let excludeLocations = filterData.locationSelectedList?.length ?
      filterData.locationSelectedList?.filter(j => !!j.isExcluded).map(j => j.key) : [];
    if (("talent-insights" === extraInfo.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.locationSelectedList?.length)
      excludeLocations =  excludeLocations.concat(redirectDataTI.locationSelectedList.filter(j => !!j.isExcluded).map(j => j.key));
    return _.uniq(excludeLocations);
  }

  // private getSelectedCompanies(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
  //   if(this.talentInsightsReqHandlerService.isCompanyInsightsTab)
  // }

  private getExcludeCompanies(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let excludeCompanies = filterData.companySelectedList?.length ?
      filterData.companySelectedList?.filter(j => !!j.isExcluded).map(j => j.key) : [];
    if (("talent-insights" === extraInfo.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.companySelectedList?.length)
      excludeCompanies =  excludeCompanies.concat(redirectDataTI.companySelectedList.filter(j => !!j.isExcluded).map(j => j.key));
    return _.uniq(excludeCompanies);
  }

  private getExcludeIndustries(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let excludeIndustries = filterData.industrySelectedList?.length ?
      filterData.industrySelectedList?.filter(j => !!j.isExcluded).map(j => j.key) : [];
    if (("talent-insights" === extraInfo.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.industrySelectedList?.length)
      excludeIndustries =  excludeIndustries.concat(redirectDataTI.industrySelectedList.filter(j => !!j.isExcluded).map(j => j.key));
    return _.uniq(excludeIndustries);
  }

  private getExcludSchoolLocation(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let excludeSchoolLocations = filterData.schoolLocationSelectedList?.length ?
      filterData.schoolLocationSelectedList?.filter(j => !!j.isExcluded).map(j => j.key) : [];
    if (("talent-insights" === extraInfo.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.schoolLocationSelectedList?.length)
    excludeSchoolLocations =  excludeSchoolLocations.concat(redirectDataTI.schoolLocationSelectedList.filter(j => !!j.isExcluded).map(j => j.key));
    return _.uniq(excludeSchoolLocations);
  }

  private getExcludeSkills(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
        let excludeSkills = filterData.skillsSelectedList?.length ?
      filterData.skillsSelectedList?.filter(s => (!!s.isExcluded && s.isMandatory == false)).map(s => s.isSuggested && !s.key.includes('.aisearch') ? `${s.key}.aisearch` : s.key) : [];
    if (("talent-insights" === extraInfo?.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.skillsSelectedList?.length)
      excludeSkills =  excludeSkills.concat(redirectDataTI.skillsSelectedList.filter(s => (!!s.isExcluded && s.isMandatory == false)).map(s => s.isSuggested && !s.key.includes('.aisearch') ? `${s.key}.aisearch` : s.key));
    return _.uniq(excludeSkills);
  }

  private getExcludeMandatorySkills(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
         let excludeSkills = filterData.skillsSelectedList?.length ?
      filterData.skillsSelectedList?.filter(s => (!!s.isExcluded && s.isMandatory == true)).map(s => s.isSuggested && !s.key.includes('.aisearch') ? `${s.key}.aisearch` : s.key) : [];
    if (("talent-insights" === extraInfo?.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.skillsSelectedList?.length)
      excludeSkills =  excludeSkills.concat(redirectDataTI.skillsSelectedList.filter(s => (!!s.isExcluded && s.isMandatory == true)).map(s => s.isSuggested && !s.key.includes('.aisearch') ? `${s.key}.aisearch` : s.key));
    return _.uniq(excludeSkills);
  }

  private getExcludeFunctions(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
      let excludeFunctions = filterData.functionSelectedList?.length ?
      filterData.functionSelectedList?.filter(j => !!j.isExcluded).map(j => j.key) : [];
    if (("talent-insights" === extraInfo?.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.functionSelectedList?.length)
      excludeFunctions =  excludeFunctions.concat(redirectDataTI.functionSelectedList.filter(j => !!j.isExcluded).map(j => j.key));
    return _.uniq(excludeFunctions);
  }

  private getCompanySizes(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    const companySizeFilterData = filterData.companysize ? _.flatten(filterData.companysize) : [];
    const companySizeTI = ("talent-insights" === extraInfo.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.companysize?.length ? _.flatten(redirectDataTI.companysize) : [];
    if (companySizeFilterData?.length) {
      return companySizeFilterData;
    }
    if (companySizeTI.length)
      return companySizeTI;
    return [];
  }

  private getBooleanQuery(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    // if ('talent-insights' === extraInfo.redirectSource && redirectDataTI?.keywordSelectedList?.length) {
    //   return redirectDataTI.keywordSelectedList.filter(Boolean).map(el => el.key);
    // }
    // if (this.filterDataHandlerService.isReportDetailsPage && filterData?.keywordSelectedList?.length) {
    //   return filterData.keywordSelectedList.filter(Boolean).map(el => el.key);
    // }
    return filterData.booleanquery ? filterData.booleanquery : "*";
  }

  private getExcludePastJobTitles(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let excludePastJobTitles = filterData?.pastJobTitleSelectedList?.length ?
      filterData.pastJobTitleSelectedList?.filter(j => !!j.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key) : [];
    if (("talent-insights" === extraInfo.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.pastJobTitleSelectedList?.length)
      excludePastJobTitles =  excludePastJobTitles.concat(redirectDataTI.pastJobTitleSelectedList.filter(j => !!j.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key));
    return _.uniq(excludePastJobTitles);
  }

  manadatoryApplicable(extraInfo: ExtraInfo) {
    return (
      extraInfo?.redirectSource == 'talent-insights' ||
      extraInfo?.redirectSource == 'company-insights'
    );
  }

  getTotalAggs(statusMap?: StatusMap) {
    if (!statusMap || _.isEmpty(statusMap)) {
      statusMap = this.getStatusMap(this.filterDataHandlerService.fetchFilterData);
    }

    const aggObservations : Observable<any>[] = [
      this.getAggregationReport('location_name-location_continent'),
      this.getAggregationReport('skills'),
      this.getAggregationReport('currentyearsofexperience'),
      this.getAggregationReport('education'),
      this.getAggregationReport('school'),
      this.getAggregationReport('fieldofstudy'),
      this.getAggregationReport('seniority'),
      this.getAggregationReport('gender'),
      this.getAggregationReport('school_location'),
      // this.getAggregationReport('nationality'),
      // this.getAggregationReport('ethnicity')
    ];
    if (statusMap.jobTitleStatus === CallStage.CURRENT_OR_PAST.toString()) aggObservations.push(this.getAggregationReport('jobtitle'));
    if (statusMap.jobTitleStatus === CallStage.CURRENT.toString()) aggObservations.push(this.getAggregationReport('current_jobtitle'));
    if (statusMap.jobTitleStatus === CallStage.PAST.toString()) aggObservations.push(this.getAggregationReport('pastjobtitle'));

    if (statusMap.companyStatus === CallStage.CURRENT_OR_PAST.toString())  aggObservations.push(this.getAggregationReport('company'));
    if (statusMap.companyStatus === CallStage.CURRENT.toString())  aggObservations.push(this.getAggregationReport('current_company'));
    if (statusMap.companyStatus === CallStage.PAST.toString())  aggObservations.push(this.getAggregationReport('pastcompany'));

    if (statusMap.industryStatus === CallStage.CURRENT_OR_PAST.toString()) aggObservations.push(this.getAggregationReport('industry'));
    if (statusMap.industryStatus === CallStage.CURRENT.toString()) aggObservations.push(this.getAggregationReport('current_industry'));
    if (statusMap.industryStatus === CallStage.PAST.toString()) aggObservations.push(this.getAggregationReport('pastindustry'));

    if (statusMap.yearsOfExperienceType === 'Total') aggObservations.push(this.getAggregationReport('totalyearsofexperience'));
    if (statusMap.yearsOfExperienceType === 'Average') aggObservations.push(this.getAggregationReport('averageyearsofexperience'));

    this.loaderEventService.emitLoaderObject(LoaderType.INSIGHTS);

    aggObservations.forEach(obs => obs.subscribe(res => this.emitInsightsObject(res)));
  }

    getTotalAggsAISourcing(projectnumber: string) {


    const aggObservations : Observable<any>[] = [
      this.getAggregationReportAI('location_name-location_continent', projectnumber),
      this.getAggregationReportAI('skills', projectnumber),
      this.getAggregationReportAI('currentyearsofexperience', projectnumber),
      this.getAggregationReportAI('education', projectnumber),
      this.getAggregationReportAI('school', projectnumber),
      this.getAggregationReportAI('fieldofstudy', projectnumber),
      this.getAggregationReportAI('seniority', projectnumber),
      this.getAggregationReportAI('gender', projectnumber),
      this.getAggregationReportAI('school_location', projectnumber),
      // this.getAggregationReportAI('nationality', projectnumber),
      // this.getAggregationReportAI('ethnicity', projectnumber),
      this.getAggregationReportAI('current_jobtitle', projectnumber),
      this.getAggregationReportAI('current_company', projectnumber),
      this.getAggregationReportAI('current_industry', projectnumber),
      this.getAggregationReportAI('totalyearsofexperience', projectnumber),
      this.getAggregationReportAI('currentyearsofexperience', projectnumber)
    ];
    // if (statusMap.jobTitleStatus === CallStage.CURRENT_OR_PAST.toString()) aggObservations.push(this.getAggregationReport('jobtitle'));
    // if (statusMap.jobTitleStatus === CallStage.CURRENT.toString()) aggObservations.push(this.getAggregationReport('current_jobtitle'));
    // if (statusMap.jobTitleStatus === CallStage.PAST.toString()) aggObservations.push(this.getAggregationReport('pastjobtitle'));

    // if (statusMap.companyStatus === CallStage.CURRENT_OR_PAST.toString())  aggObservations.push(this.getAggregationReport('company'));
    // if (statusMap.companyStatus === CallStage.CURRENT.toString())  aggObservations.push(this.getAggregationReport('current_company'));
    // if (statusMap.companyStatus === CallStage.PAST.toString())  aggObservations.push(this.getAggregationReport('pastcompany'));

    // if (statusMap.industryStatus === CallStage.CURRENT_OR_PAST.toString()) aggObservations.push(this.getAggregationReport('industry'));
    // if (statusMap.industryStatus === CallStage.CURRENT.toString()) aggObservations.push(this.getAggregationReport('current_industry'));
    // if (statusMap.industryStatus === CallStage.PAST.toString()) aggObservations.push(this.getAggregationReport('pastindustry'));

    // if (statusMap.yearsOfExperienceType === 'Total') aggObservations.push(this.getAggregationReport('totalyearsofexperience'));
    // if (statusMap.yearsOfExperienceType === 'Average') aggObservations.push(this.getAggregationReport('averageyearsofexperience'));

    this.loaderEventService.emitLoaderObject(LoaderType.INSIGHTS);

    aggObservations.forEach(obs => obs.subscribe(res => this.emitInsightsObject(res)));
  }

  getAggregationReportAI(aggType: string, projectnumber: string): Observable<any> {
    const localRequestObject = {
      aggregatetype: aggType,
      aggregatescount: 5

    }
        return this.aiSourcingService.getTotalAISourcingAggs(projectnumber, localRequestObject);

  }

  private getStatusMap(filterData: FilterParams) {
     return {
      jobTitleStatus : filterData.jobTitleStatus,
      companyStatus : filterData.companyStatus,
      industryStatus : filterData.industryStatus,
      yearsOfExperienceType : filterData.yearsofexperience
    };
  }

  getTableByStatus(jobTitleStatus: string) {
    switch (jobTitleStatus) {
      case CallStage.CURRENT_OR_PAST.toString():
        return "jobtitle";
      case CallStage.PAST.toString():
        return "pastjobtitle";
      default:
        return "current_jobtitle";
    }
  }

  private createCompanyData(companies: any[]) {
    const CharForCurrentCompany = [];
    companies.forEach((x) => {
      let dataObj = {
        label: `${x.key.charAt(0).toUpperCase()}${x.key.slice(1)}`,
        value: x.doc_count,
        tooltext: `${x.key.charAt(0).toUpperCase()}${x.key.slice(
          1
        )} <br> Talent count: ${x.doc_count}`,
        companylogo: x.companylogo,
        name: x.name,
        industry: x.industry,
        location: x.location,
        size: x.size,
        website: x.website,
        linkedin_url: x.linkedin_url,
        tags: x.tags,
        twitter_url: x.twitter_url,
        github_url: x.github_url,
        facebook_url: x.facebook_url,
      };
      CharForCurrentCompany.push(dataObj);
    });

    const dataSourceCurrentCompany = {
      type: "column2d",
      dataFormat: "json",
      height: 14,
      width: 14,
      chart: {
        renderAt: "chart-container",
        paletteColors: "#5c63a2",
        showPercentValues: "1",
        yAxisNameFontColor: "#2C2C2C",
        legendPosition: "right",
        theme: "fusion",
        showValues: "1",
        labelFont: "Poppins",
        legendItemFont: "Poppins",
        legendItemFontSize: "12",
      },
      data: CharForCurrentCompany,
    };
    return dataSourceCurrentCompany;
  }

  createWorkCountryData(response) {
    const CharForworkCountry = [];
    CharForworkCountry.push(["Location", "Talent count"]);
    response['location_name-location_continent']?.slice(0, 10).forEach((x) => {
      const dataObj = [
        `${x.key.charAt(0).toUpperCase()}${x.key.slice(1)}`,
        x.doc_count,

      ];
      CharForworkCountry.push(dataObj);
    });
    return CharForworkCountry;
  }

  private createSeniorityData(response) {
    const CharForSeniority = [];

    const dataSourceSeniority = this.fusionChartService.seniorityChart(response?.seniority,'personSearchInsights');

    return dataSourceSeniority;
  }

  private createCurrentExperience(response, type) {
    const dataSourceCurrentExperience = this.fusionChartService.yearsOfExperience(response[type], 'personSearchInsights')
    return dataSourceCurrentExperience;
  }

  private createExpInCurrentRole(response: any) {
    const dataSourceExpInCurrentRole = this.fusionChartService.yearsOfExperience(response?.currentyearsofexperience, 'personSearchInsights');
    return dataSourceExpInCurrentRole;
  }

  createDataSourceGender(resp) {
    const response = Object.assign({}, resp);
    let dataSourceGender;
    if(this.aiSourcingService.isAISourcingInsightsTab) {
     dataSourceGender = this.fusionChartService.genderDiversityChart(response?.gender);
    } else {
     dataSourceGender = this.fusionChartService.genderDiversityChart(response?.gender, 'personSearchInsights');
    }
    return Object.assign({}, dataSourceGender);
  }

  /**
   * Legacy method. Replaced with V2
   */
  getTalentInsightsRedirectData() {
    let redirectDataTS: FilterParams;
    try {
      redirectDataTS = JSON.parse(this.localStorageService.getItem(GeneralConstants.LS_KEYS.TALENT_FILTERS));
    } catch (error) {
      console.log('error while parsing reidrect data for talent insights', redirectDataTS);
    }
    return redirectDataTS;
  }

  getTalentInsightsRedirectDataV2() {
    let redirectDataTI: FilterParams;
    try {
      const uniqueHash = this.activatedRoute.snapshot.queryParamMap.get(GeneralConstants.LS_KEYS.UNIQUE_HASH);
      const key = `${GeneralConstants.LS_KEYS.TALENT_FILTERS}-${uniqueHash}`;
      redirectDataTI = JSON.parse(this.sessionStorageService.getItem(key));
    } catch (error) {
      console.log('error while parsing reidrect data using hash key for talent insights', error);
    }
    return redirectDataTI;
  }

  getTalentInsightsMandatoryData() : any {
    let redirectData = {};
    try {
      const uniqueHash = this.activatedRoute.snapshot.queryParamMap.get(GeneralConstants.LS_KEYS.UNIQUE_HASH );
      const key = `talentfilters-${uniqueHash}`;
      redirectData = JSON.parse(this.sessionStorageService.getItem(key));
    } catch (error) {
      console.log('error while fetching mandatory data for TI', error);
    }
    return redirectData;
  }

  getCompanyInsightsRedirection() {
    let redirectDataTS: FilterParams;
    try {
      redirectDataTS = JSON.parse(this.localStorageService.getItem(GeneralConstants.LS_KEYS.COMPANY_FILTERS));
    } catch (error) {
      console.log('error while parsing reidrect data for talent insights', redirectDataTS);
    }
    return redirectDataTS;
  }

  getCompanyInsightsRedirectDataV2() {
    let redirectDataTI: FilterParams;
    try {
      const uniqueHash = this.activatedRoute.snapshot.queryParamMap.get(GeneralConstants.LS_KEYS.UNIQUE_HASH_COMPANY);
      const key = `${GeneralConstants.LS_KEYS.COMPANY_FILTERS}-${uniqueHash}`;
      redirectDataTI = JSON.parse(this.sessionStorageService.getItem(key));
    } catch (error) {
      console.log('error while parsing reidrect data using hash key for talent insights', error);
    }
    return redirectDataTI;
  }

  getCompanyInsightsMandatoryData() : any {
    let redirectData = {};
    try {
      const uniqueHash = this.activatedRoute.snapshot.queryParamMap.get(GeneralConstants.LS_KEYS.UNIQUE_HASH_COMPANY );
    const key = `companyfilters-${uniqueHash}`;
      redirectData = JSON.parse(this.sessionStorageService.getItem(key));
    } catch (error) {
      console.log('error while fetching mandatory data for TI', error);
    }
    return redirectData;
  }

  private locationTopList: any[] = [];

  getLocationTopList() {
    return this.locationTopList;
  }

  setLocationTopList(list: any) {
    this.locationTopList = list;
  }

  private companyTopList: any[] = [];

  getCompanyTopList() {
    return this.companyTopList;
  }

  setCompanyTopList(list: any) {
    this.companyTopList = list;
  }

  private continentDataSubject: Subject<any> = new Subject();

  get getContinentEvent(): Observable<any> {
    return this.continentDataSubject.asObservable();
  }

  emitContinentEvent(continentData: any) {
    this.continentDataSubject.next(continentData);
  }


}
